import React, { type FunctionComponent } from 'react'
import { Tree, fontSize, fontWeight, spacing } from '@retailer-platform/shared-components'
import styled from '@emotion/styled'
import { LoadingLockupTextBase } from '@instacart/ids-core'
import range from 'lodash/range'
import { useDomainMessage } from 'domains/products/utils/domain/intl'
import { useGetRetailerDepartmentsQuery } from 'domains/products/api'
import { useGetDepartmentsAndAislesTreeNodes } from 'domains/products/components/search/utils/useGetDepartmentsAndAislesTreeNodes.hooks'

const loadingStyles = {
  container: {
    height: 25,
    marginTop: 8,
    marginBottom: 8,
    width: 'calc(100% - 10px)',
  },
}

const StyledContainer = styled.div({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',

  h3: {
    fontSize: fontSize.X15,
    fontWeight: fontWeight.BOLD,
    marginBottom: spacing.X20,
  },

  '.tree-item-icon': {
    display: 'none',
  },
})

const StyledScrollablePane = styled.div({
  flex: 1,
  overflowY: 'scroll',
  overflowX: 'hidden',
})

interface ProductsSearchDepartmentsAndAislesTreeProps
  extends Pick<React.ComponentProps<typeof Tree>, 'onClickItem' | 'initialActiveKey'> {
  retailerId: string
  departmentId: string
  aisleId: string
  children?: never
}

export const ProductsSearchDepartmentsAndAislesTree: FunctionComponent<
  ProductsSearchDepartmentsAndAislesTreeProps
> = ({ retailerId, departmentId, aisleId, ...restProps }) => {
  const title = useDomainMessage('productsDomain.search.filters.departmentsAndAisles')
  const { data, loading } = useGetRetailerDepartmentsQuery({ variables: { retailerId } })
  const treeNodes = useGetDepartmentsAndAislesTreeNodes(data?.retailer?.departments)
  const combination = [departmentId, aisleId].filter(Boolean)

  return (
    <StyledContainer>
      <h3>{title}</h3>

      <StyledScrollablePane>
        {loading ? (
          range(10).map(i => <LoadingLockupTextBase key={i} styles={loadingStyles} />)
        ) : (
          <Tree
            {...restProps}
            highlightActive={true}
            data={treeNodes}
            initialActiveKey={combination.join('/') || undefined}
            initialOpenNodes={combination}
          />
        )}
      </StyledScrollablePane>
    </StyledContainer>
  )
}
